import { Drawer as AntDrawer, DrawerProps as AntDrawerProps } from 'antd'
import { Button } from '@/components/ui/Button'
import { CloseOutlined } from '@ant-design/icons'
import { ReactNode } from 'react'

export interface DrawerProps extends Omit<AntDrawerProps, 'footer'> {
  /**
   * Drawer title
   */
  title: string | ReactNode

  /**
   * Whether the drawer is visible
   */
  open: boolean

  /**
   * Close handler
   */
  onClose: () => void

  /**
   * Submit handler (optional)
   */
  onSubmit?: () => void

  /**
   * Submit button text
   * @default 'Save'
   */
  submitText?: string

  /**
   * Cancel button text
   * @default 'Cancel'
   */
  cancelText?: string

  /**
   * Loading state
   */
  loading?: boolean

  /**
   * Drawer content
   */
  children: ReactNode

  /**
   * Show footer with action buttons
   * @default true
   */
  showFooter?: boolean

  /**
   * Show cancel button in footer
   * @default true
   */
  showCancelButton?: boolean

  /**
   * Custom footer content (overrides default footer)
   */
  customFooter?: ReactNode

  /**
   * Drawer width
   * @default 500
   */
  width?: number | string

  /**
   * Extra content in the header
   */
  extra?: ReactNode
}

/**
 * Drawer Component
 *
 * A panel that slides in from the right side of the screen, typically used for forms or detailed views.
 *
 * @example
 * <Drawer
 *   title="Edit User"
 *   open={isOpen}
 *   onClose={handleClose}
 *   onSubmit={handleSubmit}
 * >
 *   <Input label="Name" />
 *   <Input label="Email" />
 * </Drawer>
 *
 * @example
 * // Drawer without footer
 * <Drawer
 *   title="User Details"
 *   open={isOpen}
 *   onClose={handleClose}
 *   showFooter={false}
 * >
 *   <p>View only content</p>
 * </Drawer>
 */
export function Drawer({
  title,
  open,
  onClose,
  onSubmit,
  submitText = 'Save',
  cancelText = 'Cancel',
  loading = false,
  children,
  showFooter = true,
  showCancelButton = true,
  customFooter,
  width = 500,
  extra,
  ...props
}: DrawerProps) {
  // Default footer with Cancel and Save buttons
  const defaultFooter = showFooter ? (
    <div className="flex justify-end gap-3">
      {showCancelButton && (
        <Button variant="secondary" size="small" onClick={onClose} disabled={loading}>
          {cancelText}
        </Button>
      )}
      {onSubmit && (
        <Button variant="primary" size="small" onClick={onSubmit} loading={loading}>
          {submitText}
        </Button>
      )}
    </div>
  ) : null

  const footer = customFooter !== undefined ? customFooter : defaultFooter

  return (
    <AntDrawer
      title={title}
      open={open}
      onClose={onClose}
      width={width}
      closeIcon={<CloseOutlined />}
      footer={footer}
      extra={extra}
      placement="right"
      {...props}
    >
      {children}
    </AntDrawer>
  )
}
